import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink } from "react-router-dom";

import {
	actualizarCampanaPartes,
	crearCampanaPartes,
	eliminarCampanaPartes,
	getCampanasPartes,
} from "../../services/apis";
import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import { formatDateTimeEs } from "../../utils/formatters";

const createInitialForm = () => ({
	id: null,
	nombre: "",
	descripcion: "",
	fecha_inicio: "",
	fecha_fin: "",
	activa: true,
	es_sistema: false,
	delegaciones_ids: [],
	clientes_ids: [],
});

const buildExpandedDelegacionesState = (delegacionesIds = [], clientesIds = [], clientes = []) => {
	const expandedState = {};
	const selectedDelegaciones = Array.isArray(delegacionesIds) ? delegacionesIds : [];
	const selectedClientes = new Set(Array.isArray(clientesIds) ? clientesIds : []);

	selectedDelegaciones.forEach((delegacionId) => {
		const normalizedId = Number(delegacionId);
		if (normalizedId > 0) {
			expandedState[normalizedId] = true;
		}
	});

	(Array.isArray(clientes) ? clientes : []).forEach((cliente) => {
		const clienteId = Number(cliente?.id || 0);
		const delegacionId = Number(cliente?.id_delegacion || 0);

		if (clienteId > 0 && delegacionId > 0 && selectedClientes.has(clienteId)) {
			expandedState[delegacionId] = true;
		}
	});

	return expandedState;
};

const getDelegacionClientIds = (clientes = [], delegacionId) =>
	(Array.isArray(clientes) ? clientes : [])
		.filter((cliente) => Number(cliente?.id_delegacion || 0) === Number(delegacionId || 0))
		.map((cliente) => Number(cliente?.id || 0))
		.filter((id) => id > 0);

const buildDelegacionesIdsFromClientes = (delegaciones = [], clientes = [], selectedClientesIds = []) => {
	const selectedClientesSet = new Set(
		(Array.isArray(selectedClientesIds) ? selectedClientesIds : [])
			.map((id) => Number(id))
			.filter((id) => id > 0)
	);

	return (Array.isArray(delegaciones) ? delegaciones : [])
		.map((delegacion) => {
			const delegacionId = Number(delegacion?.id || 0);
			const delegacionClientIds = getDelegacionClientIds(clientes, delegacionId);

			if (delegacionId <= 0 || delegacionClientIds.length === 0) {
				return 0;
			}

			return delegacionClientIds.every((clienteId) => selectedClientesSet.has(clienteId))
				? delegacionId
				: 0;
		})
		.filter((id) => id > 0);
};

export const Campanas = () => {
	const [campanas, setCampanas] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");
	const [expandedId, setExpandedId] = useState(null);
	const [partsByCampaign, setPartsByCampaign] = useState({});
	const [loadingPartsId, setLoadingPartsId] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [loadingDialogData, setLoadingDialogData] = useState(false);
	const [form, setForm] = useState(createInitialForm());
	const [delegaciones, setDelegaciones] = useState([]);
	const [clientes, setClientes] = useState([]);
	const [expandedDelegacionesDialog, setExpandedDelegacionesDialog] = useState({});

	const loadCampanas = useCallback(async () => {
		setLoaded(false);
		setError("");

		try {
			const response = await getCampanasPartes();

			if (response?.success !== true) {
				setCampanas([]);
				setError(response?.message || "No se pudieron cargar las campanas.");
				return;
			}

			const payload = response?.data || {};
			setCampanas(Array.isArray(payload?.campanas) ? payload.campanas : []);
		} catch (loadError) {
			console.error("Error cargando campanas de partes:", loadError);
			setCampanas([]);
			setError("No se pudieron cargar las campanas.");
		} finally {
			setLoaded(true);
		}
	}, []);

	useEffect(() => {
		void loadCampanas();
	}, [loadCampanas]);

	const loadDialogAssignments = useCallback(async (campaignId = 0) => {
		setLoadingDialogData(true);

		try {
			const response = await getCampanasPartes({
				id_campana: campaignId > 0 ? campaignId : undefined,
				include_assignments: true,
			});

			if (response?.success !== true) {
				throw new Error(response?.message || "No se pudieron cargar las asignaciones.");
			}

			const payload = response?.data || {};
			setDelegaciones(Array.isArray(payload?.delegaciones) ? payload.delegaciones : []);
			setClientes(Array.isArray(payload?.clientes) ? payload.clientes : []);

			return payload;
		} finally {
			setLoadingDialogData(false);
		}
	}, []);

	const openCreateDialog = async () => {
		setForm(createInitialForm());
		setExpandedDelegacionesDialog({});
		setDialogOpen(true);
		setMessage("");
		setError("");

		try {
			await loadDialogAssignments();
		} catch (loadError) {
			console.error("Error cargando asignaciones para nueva campana:", loadError);
			setError("No se pudieron cargar delegaciones y clientes.");
		}
	};

	const openEditDialog = async (campana) => {
		const campaignId = Number(campana?.id || 0);
		setDialogOpen(true);
		setMessage("");
		setError("");
		setExpandedDelegacionesDialog({});

		try {
			const payload = await loadDialogAssignments(campaignId);
			const delegacionesPayload = Array.isArray(payload?.delegaciones) ? payload.delegaciones : [];
			const clientesPayload = Array.isArray(payload?.clientes) ? payload.clientes : [];
			const selectedClientesIds = clientesPayload
			.filter((cliente) => Number(cliente?.id_campana || 0) === campaignId)
			.map((cliente) => Number(cliente?.id || 0))
			.filter((id) => id > 0);
			const selectedDelegacionesIds = buildDelegacionesIdsFromClientes(
				delegacionesPayload,
				clientesPayload,
				selectedClientesIds
			);

			setForm({
				id: campaignId || null,
				nombre: String(campana?.nombre || ""),
				descripcion: String(campana?.descripcion || ""),
				fecha_inicio: String(campana?.fecha_inicio || ""),
				fecha_fin: String(campana?.fecha_fin || ""),
				activa: campana?.activa !== false,
				es_sistema: campana?.es_sistema === true,
				delegaciones_ids: selectedDelegacionesIds,
				clientes_ids: selectedClientesIds,
			});
			setExpandedDelegacionesDialog(
				buildExpandedDelegacionesState(
					selectedDelegacionesIds,
					selectedClientesIds,
					clientesPayload
				)
			);
		} catch (loadError) {
			console.error("Error cargando asignaciones para editar campana:", loadError);
			setDialogOpen(false);
			setError("No se pudieron cargar delegaciones y clientes de la campana.");
		}
	};

	const closeDialog = () => {
		if (saving) return;
		setDialogOpen(false);
		setForm(createInitialForm());
		setExpandedDelegacionesDialog({});
	};

	const handleSave = async () => {
		if (!form.nombre.trim()) {
			setError("Debes indicar un nombre para la campana.");
			return;
		}

		if (!form.fecha_inicio || !form.fecha_fin) {
			setError("Debes indicar fecha de inicio y de fin.");
			return;
		}

		setSaving(true);
		setError("");
		setMessage("");

		try {
			const payload = {
				...form,
				nombre: form.nombre.trim(),
				descripcion: form.descripcion.trim(),
				activa: form.activa ? 1 : 0,
				delegaciones_ids: Array.isArray(form.delegaciones_ids)
					? form.delegaciones_ids.map((id) => Number(id)).filter((id) => id > 0)
					: [],
				clientes_ids: Array.isArray(form.clientes_ids)
					? form.clientes_ids.map((id) => Number(id)).filter((id) => id > 0)
					: [],
			};

			const response = form.id
				? await actualizarCampanaPartes(payload)
				: await crearCampanaPartes(payload);

			if (response?.success !== true) {
				setError(response?.message || "No se pudo guardar la campana.");
				return;
			}

			setMessage(response?.message || "Campana guardada correctamente.");
			setDialogOpen(false);
			setForm(createInitialForm());
			setExpandedDelegacionesDialog({});
			await loadCampanas();
		} catch (saveError) {
			console.error("Error guardando campana:", saveError);
			setError("No se pudo guardar la campana.");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (campana) => {
		if (!campana?.id || campana?.es_sistema === true) {
			return;
		}

		setError("");
		setMessage("");

		try {
			const response = await eliminarCampanaPartes(campana.id);

			if (response?.success !== true) {
				setError(response?.message || "No se pudo eliminar la campana.");
				return;
			}

			if (expandedId === campana.id) {
				setExpandedId(null);
			}

			setPartsByCampaign((current) => {
				const next = { ...current };
				delete next[campana.id];
				return next;
			});
			setMessage(response?.message || "Campana eliminada correctamente.");
			await loadCampanas();
		} catch (deleteError) {
			console.error("Error eliminando campana:", deleteError);
			setError("No se pudo eliminar la campana.");
		}
	};

	const toggleExpand = async (campana) => {
		const nextId = expandedId === campana.id ? null : campana.id;
		setExpandedId(nextId);

		if (!nextId || partsByCampaign[nextId]) {
			return;
		}

		setLoadingPartsId(nextId);

		try {
			const response = await getCampanasPartes({
				id_campana: nextId,
				include_parts: true,
			});

			if (response?.success !== true) {
				setError(response?.message || "No se pudieron cargar los partes de la campana.");
				return;
			}

			setPartsByCampaign((current) => ({
				...current,
				[nextId]: Array.isArray(response?.data?.campana?.partes)
					? response.data.campana.partes
					: [],
			}));
		} catch (partsError) {
			console.error("Error cargando partes de la campana:", partsError);
			setError("No se pudieron cargar los partes de la campana.");
		} finally {
			setLoadingPartsId(null);
		}
	};

	const orderedCampanas = useMemo(
		() => [...campanas].sort((left, right) => {
			if (left.es_sistema !== right.es_sistema) {
				return left.es_sistema ? 1 : -1;
			}
			if (left.activa !== right.activa) {
				return left.activa ? -1 : 1;
			}
			return String(right.fecha_inicio || "").localeCompare(String(left.fecha_inicio || ""));
		}),
		[campanas]
	);

	const delegacionesOrdenadas = useMemo(
		() =>
			[...delegaciones].sort((left, right) =>
				String(left?.nombre || "").localeCompare(String(right?.nombre || ""), "es")
			),
		[delegaciones]
	);

	const clientesPorDelegacion = useMemo(() => {
		const groupedClientes = new Map();

		clientes.forEach((cliente) => {
			const delegacionId = Number(cliente?.id_delegacion || 0);
			if (delegacionId <= 0) {
				return;
			}

			const currentClientes = groupedClientes.get(delegacionId) || [];
			currentClientes.push(cliente);
			groupedClientes.set(delegacionId, currentClientes);
		});

		groupedClientes.forEach((groupedItems, delegacionId) => {
			groupedClientes.set(
				delegacionId,
				[...groupedItems].sort((left, right) =>
					String(left?.nombre || "").localeCompare(String(right?.nombre || ""), "es")
				)
			);
		});

		return groupedClientes;
	}, [clientes]);

	const clientesSinDelegacion = useMemo(
		() =>
			[...clientes]
				.filter((cliente) => Number(cliente?.id_delegacion || 0) <= 0)
				.sort((left, right) =>
					String(left?.nombre || "").localeCompare(String(right?.nombre || ""), "es")
				),
		[clientes]
	);

	const handleToggleDelegacionDialog = (delegacionId) => {
		setExpandedDelegacionesDialog((current) => ({
			...current,
			[delegacionId]: !current[delegacionId],
		}));
	};

	const handleToggleDelegacionAsignada = (delegacionId, checked) => {
		const normalizedDelegacionId = Number(delegacionId || 0);
		if (normalizedDelegacionId <= 0) {
			return;
		}

		setForm((current) => {
			const currentClientesIds = Array.isArray(current?.clientes_ids)
				? current.clientes_ids.map((id) => Number(id)).filter((id) => id > 0)
				: [];
			const delegacionClientIds = getDelegacionClientIds(clientes, normalizedDelegacionId);
			const nextClientesSet = new Set(currentClientesIds);

			if (checked) {
				delegacionClientIds.forEach((clienteId) => {
					nextClientesSet.add(clienteId);
				});
			} else {
				delegacionClientIds.forEach((clienteId) => {
					nextClientesSet.delete(clienteId);
				});
			}

			const nextClientesIds = Array.from(nextClientesSet);

			return {
				...current,
				delegaciones_ids: buildDelegacionesIdsFromClientes(
					delegaciones,
					clientes,
					nextClientesIds
				),
				clientes_ids: nextClientesIds,
			};
		});
	};

	const handleToggleClienteAsignado = (clienteId, checked) => {
		const normalizedClienteId = Number(clienteId || 0);
		if (normalizedClienteId <= 0) {
			return;
		}

		setForm((current) => {
			const currentClientesIds = Array.isArray(current?.clientes_ids)
				? current.clientes_ids.map((id) => Number(id)).filter((id) => id > 0)
				: [];
			const nextClientesSet = new Set(currentClientesIds);

			if (checked) {
				nextClientesSet.add(normalizedClienteId);
			} else {
				nextClientesSet.delete(normalizedClienteId);
			}

			return {
				...current,
				clientes_ids: Array.from(nextClientesSet),
				delegaciones_ids: buildDelegacionesIdsFromClientes(
					delegaciones,
					clientes,
					Array.from(nextClientesSet)
				),
			};
		});
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Campañas de partes" />
			</Grid>

			<Grid container spacing={1.5} sx={{ px: 2, pb: 2 }} alignItems="center">
				<Button variant="text" startIcon={<ArrowBackIcon />} component={RouterLink} to="/cuadrillas">
					Volver a partes
				</Button>
				<Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
					Nueva campaña
				</Button>
			</Grid>

			{!!message && (
				<Grid sx={{ px: 2, pb: 2 }}>
					<Alert severity="success">{message}</Alert>
				</Grid>
			)}

			{!!error && (
				<Grid sx={{ px: 2, pb: 2 }}>
					<Alert severity="error">{error}</Alert>
				</Grid>
			)}

			<Grid sx={{ px: 2, pb: 3 }}>
				<Paper variant="outlined">
					{!loaded ? (
						<Box
							sx={{
								minHeight: "32vh",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 1,
							}}
						>
							<CircularProgress size={20} />
							<Typography variant="body2">Cargando campanas...</Typography>
						</Box>
					) : orderedCampanas.length === 0 ? (
						<Box sx={{ p: 3 }}>
							<Typography variant="body2">
								No hay campanas creadas todavia.
							</Typography>
						</Box>
					) : (
						<Box>
							{orderedCampanas.map((campana) => {
								const parts = partsByCampaign[campana.id] || [];
								const isExpanded = expandedId === campana.id;

								return (
									<Box
										key={`campana-${campana.id}`}
										sx={{
											borderBottom: "1px solid",
											borderColor: "divider",
											"&:last-of-type": {
												borderBottom: "none",
											},
										}}
									>
										<Box
											sx={{
												px: 2,
												py: 1.5,
												display: "flex",
												alignItems: "center",
												gap: 1.5,
												flexWrap: "wrap",
											}}
										>
											<IconButton size="small" onClick={() => toggleExpand(campana)}>
												{isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
											</IconButton>

											<Box sx={{ minWidth: 0, flex: 1 }}>
												<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
													{campana.nombre}
												</Typography>
												<Typography variant="body2" color="text.secondary">
													{campana.descripcion || "Sin descripcion"}
												</Typography>
											</Box>

											<Chip
												size="small"
												color={campana.activa ? "success" : "default"}
												label={campana.activa ? "Activa" : "Inactiva"}
											/>
											{campana.es_sistema && (
												<Chip size="small" variant="outlined" label="Sistema" />
											)}
											<Chip
												size="small"
												variant="outlined"
												label={`${campana.total_partes} parte(s)`}
											/>
											<Chip
												size="small"
												variant="outlined"
												label={`${campana.total_validados} validados`}
											/>
											<Chip
												size="small"
												variant="outlined"
												label={`${campana.total_revisados} revisados`}
											/>
											<Typography variant="body2" color="text.secondary">
												{campana.fecha_inicio && campana.fecha_fin
													? `${formatDateTimeEs(campana.fecha_inicio, {
															includeTime: false,
													  })} - ${formatDateTimeEs(campana.fecha_fin, {
															includeTime: false,
													  })}`
													: "Sin rango"}
											</Typography>

											<Button
												size="small"
												variant="outlined"
												startIcon={<EditIcon />}
												onClick={() => openEditDialog(campana)}
												disabled={campana.es_sistema === true}
											>
												Editar
											</Button>
											<IconButton
												size="small"
												color="error"
												onClick={() => handleDelete(campana)}
												disabled={campana.es_sistema === true}
											>
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										</Box>

										<Collapse in={isExpanded}>
											<Box sx={{ px: 2, pb: 2 }}>
												{loadingPartsId === campana.id ? (
													<Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1 }}>
														<CircularProgress size={18} />
														<Typography variant="body2">Cargando partes...</Typography>
													</Box>
												) : parts.length === 0 ? (
													<Typography variant="body2" color="text.secondary">
														No hay partes asociados a esta campana.
													</Typography>
												) : (
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell>ID</TableCell>
																<TableCell>Parte</TableCell>
																<TableCell>Fecha</TableCell>
																<TableCell>Cliente</TableCell>
																<TableCell>Cabo</TableCell>
																<TableCell>Personas</TableCell>
																<TableCell>Validado</TableCell>
																<TableCell>Revisado</TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{parts.map((parte) => (
																<TableRow key={`campana-parte-${campana.id}-${parte.id_cuadrilla}`}>
																	<TableCell>{parte.id_cuadrilla}</TableCell>
																	<TableCell>{parte.nombre_cuadrilla || "-"}</TableCell>
																	<TableCell>
																		{parte.fecha_cuadrilla
																			? formatDateTimeEs(parte.fecha_cuadrilla, {
																					includeTime: false,
																			  })
																			: "-"}
																	</TableCell>
																	<TableCell>{parte.nombre_cliente || "-"}</TableCell>
																	<TableCell>{parte.nombre_cabo || "-"}</TableCell>
																	<TableCell>{parte.total_recolectores}</TableCell>
																	<TableCell>{parte.validado ? "Si" : "No"}</TableCell>
																	<TableCell>{parte.revisado ? "Si" : "No"}</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												)}
											</Box>
										</Collapse>
									</Box>
								);
							})}
						</Box>
					)}
				</Paper>
			</Grid>

			<Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
				<DialogTitle>{form.id ? "Editar campana" : "Nueva campana"}</DialogTitle>
				<DialogContent>
					{loadingDialogData ? (
						<Box
							sx={{
								minHeight: 220,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 1,
							}}
						>
							<CircularProgress size={20} />
							<Typography variant="body2">Cargando asignaciones...</Typography>
						</Box>
					) : (
					<Grid container spacing={2} sx={{ pt: 0.5 }}>
						<Grid item size={{ xs: 12 }}>
							<TextField
								fullWidth
								label="Nombre"
								value={form.nombre}
								onChange={(event) =>
									setForm((current) => ({ ...current, nombre: event.target.value }))
								}
							/>
						</Grid>
						<Grid item size={{ xs: 12 }}>
							<TextField
								fullWidth
								label="Descripcion"
								value={form.descripcion}
								onChange={(event) =>
									setForm((current) => ({ ...current, descripcion: event.target.value }))
								}
							/>
						</Grid>
						<Grid item size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								type="date"
								label="Fecha inicio"
								value={form.fecha_inicio}
								onChange={(event) =>
									setForm((current) => ({ ...current, fecha_inicio: event.target.value }))
								}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item size={{ xs: 12, md: 6 }}>
							<TextField
								fullWidth
								type="date"
								label="Fecha fin"
								value={form.fecha_fin}
								onChange={(event) =>
									setForm((current) => ({ ...current, fecha_fin: event.target.value }))
								}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>
						<Grid item size={{ xs: 12 }}>
							<Button
								variant={form.activa ? "contained" : "outlined"}
								onClick={() =>
									setForm((current) => ({ ...current, activa: !current.activa }))
								}
								disabled={form.es_sistema === true}
							>
								{form.activa ? "Activa" : "Inactiva"}
							</Button>
						</Grid>
						<Grid item size={{ xs: 12 }}>
							<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
								Asignaciones
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
								Marca una delegacion completa o despliegala para seleccionar clientes concretos.
							</Typography>

							<Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
								{delegacionesOrdenadas.length === 0 ? (
									<Box sx={{ p: 2 }}>
										<Typography variant="body2" color="text.secondary">
											No hay delegaciones disponibles.
										</Typography>
									</Box>
								) : (
									delegacionesOrdenadas.map((delegacion) => {
										const delegacionId = Number(delegacion?.id || 0);
										const delegacionClientes = clientesPorDelegacion.get(delegacionId) || [];
										const delegacionClientIds = delegacionClientes
											.map((cliente) => Number(cliente?.id || 0))
											.filter((id) => id > 0);
										const totalClientesDelegacion = delegacionClientIds.length;
										const clientesSeleccionadosDelegacion = delegacionClientIds.filter((clienteId) =>
											form.clientes_ids.includes(clienteId)
										).length;
										const delegacionSeleccionada =
											totalClientesDelegacion > 0 &&
											clientesSeleccionadosDelegacion === totalClientesDelegacion;
										const delegacionSeleccionParcial =
											clientesSeleccionadosDelegacion > 0 &&
											clientesSeleccionadosDelegacion < totalClientesDelegacion;
										const expandida = Boolean(expandedDelegacionesDialog[delegacionId]);

										return (
											<Box
												key={`campana-dialog-delegacion-${delegacionId}`}
												sx={{
													borderBottom: "1px solid",
													borderColor: "divider",
													"&:last-of-type": { borderBottom: "none" },
												}}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														gap: 1,
														px: 1.5,
														py: 1,
													}}
												>
													<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, flex: 1 }}>
														<Checkbox
															checked={delegacionSeleccionada}
															indeterminate={delegacionSeleccionParcial}
															disabled={saving || form.es_sistema === true}
															onChange={(event) =>
																handleToggleDelegacionAsignada(
																	delegacionId,
																	event.target.checked
																)
															}
														/>
														<Box sx={{ minWidth: 0, flex: 1 }}>
															<Typography variant="body1" sx={{ fontWeight: 600 }}>
																{delegacion.nombre}
															</Typography>
															<Typography variant="caption" color="text.secondary">
																{clientesSeleccionadosDelegacion}/{delegacionClientes.length} cliente(s) seleccionados
															</Typography>
														</Box>
													</Box>

													<IconButton
														size="small"
														onClick={() => handleToggleDelegacionDialog(delegacionId)}
													>
														{expandida ? <ExpandMoreIcon /> : <ChevronRightIcon />}
													</IconButton>
												</Box>

												<Collapse in={expandida}>
													{delegacionClientes.length === 0 ? (
														<Box sx={{ px: 2, pb: 1.5 }}>
															<Typography variant="body2" color="text.secondary">
																Esta delegacion no tiene clientes asociados.
															</Typography>
														</Box>
													) : (
														<List dense disablePadding sx={{ pb: 1 }}>
															{delegacionClientes.map((cliente) => {
																const clienteId = Number(cliente?.id || 0);
																const clienteSeleccionado = form.clientes_ids.includes(clienteId);
																const referenciaCliente =
																	cliente?.ref_cliente_maestro ||
																	cliente?.id_clienteG ||
																	"";

																return (
																	<ListItemButton
																		key={`campana-dialog-cliente-${clienteId}`}
																		disabled={saving || form.es_sistema === true}
																		onClick={() =>
																			handleToggleClienteAsignado(
																				clienteId,
																				!clienteSeleccionado
																			)
																		}
																		sx={{ pl: 4 }}
																	>
																		<ListItemIcon sx={{ minWidth: 36 }}>
																			<Checkbox
																				edge="start"
																				checked={clienteSeleccionado}
																				disableRipple
																				disabled={saving || form.es_sistema === true}
																			/>
																		</ListItemIcon>
																		<ListItemText
																			primary={cliente.nombre || `Cliente ${clienteId}`}
																			secondary={referenciaCliente || "Sin referencia"}
																		/>
																	</ListItemButton>
																);
															})}
														</List>
													)}
												</Collapse>
											</Box>
										);
									})
								)}
							</Paper>
						</Grid>

						{clientesSinDelegacion.length > 0 && (
							<Grid item size={{ xs: 12 }}>
								<Typography variant="subtitle2" sx={{ mb: 1 }}>
									Clientes sin delegacion
								</Typography>
								<Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
									<List dense disablePadding>
										{clientesSinDelegacion.map((cliente) => {
											const clienteId = Number(cliente?.id || 0);
											const clienteSeleccionado = form.clientes_ids.includes(clienteId);
											const referenciaCliente =
												cliente?.ref_cliente_maestro ||
												cliente?.id_clienteG ||
												"";

											return (
												<ListItemButton
													key={`campana-dialog-cliente-sin-delegacion-${clienteId}`}
													disabled={saving || form.es_sistema === true}
													onClick={() =>
														handleToggleClienteAsignado(
															clienteId,
															!clienteSeleccionado
														)
													}
												>
													<ListItemIcon sx={{ minWidth: 36 }}>
														<Checkbox edge="start" checked={clienteSeleccionado} disableRipple />
													</ListItemIcon>
													<ListItemText
														primary={cliente.nombre || `Cliente ${clienteId}`}
														secondary={referenciaCliente || "Sin referencia"}
													/>
												</ListItemButton>
											);
										})}
									</List>
								</Paper>
							</Grid>
						)}
					</Grid>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDialog} disabled={saving}>
						Cancelar
					</Button>
					<Button variant="contained" onClick={handleSave} disabled={saving}>
						{saving ? "Guardando..." : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};
